import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "./ai-gateway";
import { enforceQuota, incrementUsage, QuotaExceededError } from "./lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

const QUESTIONS_TOOL = {
  name: "save_interview_questions",
  description:
    "Generate interview questions tailored to a specific job description AND a specific interview round. Question style, depth, and focus must match the round — a recruiter screen is NOT a hiring-manager deep-dive.",
  parameters: {
    type: "object",
    properties: {
      questions: {
        type: "array",
        minItems: 6,
        maxItems: 8,
        items: {
          type: "object",
          properties: {
            text: { type: "string", description: "The interview question, phrased as an interviewer would ask it in this specific round." },
            focus: {
              type: "string",
              enum: [
                "leadership", "conflict", "technical", "decision", "failure", "ambiguity", "stakeholder", "growth",
                "motivation", "logistics", "compensation", "culture_fit",
                "portfolio_walkthrough", "tradeoffs", "impact_measurement",
                "cross_functional", "influence",
                "strategy", "vision", "business_impact",
                "negotiation", "reverse_interview",
              ],
            },
            why: { type: "string", description: "One sentence on what this probes for, grounded in the JD and round." },
          },
          required: ["text", "focus", "why"],
          additionalProperties: false,
        },
      },
    },
    required: ["questions"],
    additionalProperties: false,
  },
};

type QuestionsResp = {
  questions: Array<{ text: string; focus: string; why: string }>;
};

// Each round has DISTINCT style + focus areas. The "avoid" list is critical:
// without it, the model defaults to STAR-style behaviourals for every round
// and recruiter/hiring-manager questions end up feeling identical.
const STAGE_GUIDANCE: Record<string, string> = {
  recruiter:
    "ROUND: Recruiter screen (30 min, non-technical). Generate 5 SHORT, conversational screening questions. ALLOWED focus: motivation, logistics, compensation, culture_fit. Cover: (1) why this specific role/company, (2) compensation expectations + range, (3) notice period / earliest start date, (4) work authorization / location / remote setup if relevant, (5) one quick deal-breaker probe (must-haves vs nice-to-haves). AVOID: STAR behaviourals, deep technical scenarios, 'tell me about a time' questions, portfolio walkthroughs, leadership philosophy. Phrase them like a recruiter would on a phone screen, not like a hiring manager.",
  hiring_manager:
    "ROUND: Hiring manager deep-dive (45–60 min). Generate 5–6 STAR-style behavioural questions tied to the JD's CORE day-to-day responsibilities and required technical depth. ALLOWED focus: leadership, conflict, technical, decision, failure, ambiguity. Each question must require a specific past example. Anchor every question to a concrete responsibility or skill named in the JD — do not ask generic 'tell me about a hard project' questions. AVOID: motivation/logistics/comp (that's the recruiter round), pure portfolio walkthroughs (that's the portfolio round), executive strategy (that's the leadership round).",
  portfolio:
    "ROUND: Portfolio / case walkthrough (60–90 min). Generate 5–6 questions that force the candidate to walk through specific past work in detail. ALLOWED focus: portfolio_walkthrough, tradeoffs, impact_measurement, technical, decision. Each question should ask them to pick a project that maps to a specific JD requirement, then probe: what trade-offs they made, what they'd do differently, how they measured impact, what data they used to decide. AVOID: hypothetical 'how would you' questions (this round is about REAL past work), motivation/comp questions, generic behavioural questions.",
  panel:
    "ROUND: Cross-functional panel (multiple interviewers from adjacent teams). Generate 5–6 questions about working ACROSS functions. ALLOWED focus: cross_functional, stakeholder, influence, conflict, ambiguity. Cover: collaborating with PM/Eng/Design/Sales/Ops counterparts, handling disagreement with peers in other functions, influencing without authority, navigating ambiguous ownership, escalation judgement. AVOID: solo IC technical depth (that's hiring manager), portfolio walkthroughs, executive strategy, motivation/comp.",
  leadership:
    "ROUND: Leadership / executive interview (with VP/C-suite). Generate 5–6 questions focused on strategic thinking and business outcomes. ALLOWED focus: strategy, vision, business_impact, leadership, decision. Cover: 30/60/90-day plan for this specific role, how they'd allocate budget/headcount, what they'd change in the org, how they think about prioritisation against business goals, how they measure their own success. Reward business framing over execution detail. AVOID: tactical 'how would you build X', recruiter-style logistics, individual project walkthroughs.",
  offer:
    "ROUND: Offer-stage prep (candidate is preparing to negotiate / accept). Generate 5 questions that prepare the candidate for the offer conversation. ALLOWED focus: negotiation, reverse_interview, compensation, culture_fit. Mix: (1–2) negotiation scenarios (counter on comp, scope, start date, equity), (2–3) high-signal REVERSE-interview questions the candidate should ask the company about culture/ramp/team health/red flags, (1) probe on how to evaluate the offer holistically. AVOID: behavioural questions about the candidate's past (that's earlier rounds), generic 'what's your weakness'.",
};

export const generateInterviewQuestions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { jdId?: string | null; stageKey?: string | null; stageId?: string | null }) => input ?? {})
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // Generating tailored questions counts against the interview_question quota.
    // The fallback path below (no JD) returns canned questions and does NOT count.
    let willCallAi = false;

    let jdText = "";
    let roleTitle: string | null = null;
    let company: string | null = null;

    if (data.jdId) {
      const { data: jd } = await supabase
        .from("job_descriptions")
        .select("jd_text, role_title, company")
        .eq("id", data.jdId)
        .eq("user_id", userId)
        .maybeSingle();
      if (jd) {
        jdText = jd.jd_text as string;
        roleTitle = jd.role_title as string | null;
        company = jd.company as string | null;
      }
    } else {
      // Fall back to most recent JD
      const { data: latest } = await supabase
        .from("job_descriptions")
        .select("jd_text, role_title, company")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (latest) {
        jdText = latest.jd_text as string;
        roleTitle = latest.role_title as string | null;
        company = latest.company as string | null;
      }
    }

    // Load interviewers attached to the selected stage (RLS scopes to user).
    type InterviewerLite = {
      name: string | null;
      interviewer_role: string | null;
      interviewer_company: string | null;
      seniority: string | null;
      headline: string | null;
      profile_data: { summary?: string | null } | null;
    };
    let interviewers: InterviewerLite[] = [];
    if (data.stageId) {
      const { data: rows } = await supabase
        .from("stage_interviewers")
        .select("name, interviewer_role, interviewer_company, seniority, headline, profile_data")
        .eq("application_stage_id", data.stageId)
        .eq("user_id", userId)
        .limit(4);
      interviewers = (rows ?? []) as InterviewerLite[];
    }
    const hasInterviewers = interviewers.length > 0;
    const interviewerBlock = hasInterviewers
      ? interviewers
          .map((i, n) => {
            const summary =
              (i.profile_data?.summary ?? i.headline ?? "").toString().slice(0, 200).trim();
            const who = [i.name, i.interviewer_role, i.interviewer_company && `@ ${i.interviewer_company}`, i.seniority && `(${i.seniority})`]
              .filter(Boolean)
              .join(" · ");
            return `${n + 1}. ${who}${summary ? ` — ${summary}` : ""}`;
          })
          .join("\n")
      : "";

    const stageGuidance = data.stageKey ? STAGE_GUIDANCE[data.stageKey] : null;


    if (!jdText) {
      // Generic fallback questions if no JD
      return {
        questions: [
          { text: "Tell me about a challenging project you led recently.", focus: "leadership", why: "General leadership probe." },
          { text: "Describe a time you handled conflict on your team.", focus: "conflict", why: "General teamwork probe." },
          { text: "Walk me through a technical decision you regret.", focus: "decision", why: "Self-awareness probe." },
          { text: "Tell me about a time you missed a deadline.", focus: "failure", why: "Accountability probe." },
          { text: "Describe a moment you had to influence without authority.", focus: "stakeholder", why: "Influence probe." },
        ],
        sourceJd: null,
        stageKey: data.stageKey ?? null,
      };
    }

    // Now we know we'll hit the AI — enforce quota first.
    willCallAi = true;
    if (willCallAi) {
      try {
        await enforceQuota(supabase, userId, CAPABILITIES.interview_question);
      } catch (e) {
        if (e instanceof QuotaExceededError)
          throw new Response(e.message, { status: e.status });
        throw e;
      }
    }

    let resp: QuestionsResp;
    try {
      resp = await callAIStructured<QuestionsResp>({
        messages: [
          {
            role: "system",
            content: `You are an expert interview coach generating high-signal questions tailored to (1) a specific job description, (2) a specific company, (3) a specific interview round, and (4) the specific interviewers in the room when provided.

HARD REQUIREMENTS:
- Different rounds have VERY different styles. A recruiter screen is conversational and logistical; a hiring-manager round is deep behavioural; a leadership round is strategic. Match the round's style exactly. Do NOT default to STAR behavioural questions for every round.
- At least 2 of the questions MUST reference the COMPANY by name and tie to something concrete about that company (its product, market position, recent moves, customer base, business model, or public reputation). Generic questions that could apply to any company are a failure.
- Every question's \`why\` field must name the round (e.g. "Recruiter screen probe — …", "Hiring-manager deep-dive — …") so the user can verify it was actually tailored to this round.
- Anchor remaining questions to specific responsibilities, skills, or domain language drawn from the JD text. No filler.
${hasInterviewers ? `- INTERVIEWERS are provided. At least 2 questions MUST be plausibly authored by a specific named interviewer based on their role/background. Name that interviewer in the \`why\` field, e.g. "Likely from Jane Doe (Eng Director) — probes architectural taste given her platform-team background."` : ""}${stageGuidance ? `\n\nROUND-SPECIFIC GUIDANCE (follow strictly):\n${stageGuidance}` : "\n\nNo specific round selected — generate a balanced mix of STAR behaviourals tied to the JD."}`,
          },
          {
            role: "user",
            content: `ROUND: ${data.stageKey ?? "general"}\nCOMPANY: ${company ?? "(unknown — skip company-specific questions)"}\nROLE: ${roleTitle ?? "Untitled role"}\n${hasInterviewers ? `\nINTERVIEWERS IN THIS ROUND:\n${interviewerBlock}\n` : ""}\nJOB DESCRIPTION:\n${jdText.slice(0, 5000)}\n\nGenerate 6 questions. Remember: at least 2 must reference ${company ?? "the company"} by name and tie to something concrete about it. Every \`why\` must name the round.${hasInterviewers ? ` At least 2 must be attributed to a specific interviewer by name in the \`why\` field.` : ""}`,
          },
        ],
        tool: QUESTIONS_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError) throw new Response(e.message, { status: e.status });
      throw e;
    }

    await incrementUsage(userId, CAPABILITIES.interview_question);

    return {
      questions: resp.questions,
      sourceJd: { id: data.jdId ?? null, role_title: roleTitle, company },
      stageKey: data.stageKey ?? null,
      interviewerCount: interviewers.length,
    };
  });

export const listJDsForInterview = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("job_descriptions")
      .select("id, role_title, company, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(20);
    if (error) throw new Response(error.message, { status: 500 });
    return { jds: data ?? [] };
  });
